/**
 * Checking what a script hands back.
 *
 * Symmetrical with `payload.ts`: that file decides what a stranger's code can
 * SEE, this one decides what it can PUT ON THE CHART. Both directions need a
 * gate, and the return direction is the one that is easy to forget — the value
 * arrives as `unknown` from a Worker message and is one careless cast away from
 * being rendered.
 *
 * Nothing here throws. A bad return is a message the trader reads next to the
 * script's name, because they are usually not the person who wrote it and
 * "somebody's indicator is broken" is the useful sentence.
 */
import type { ScriptPlotDecl } from './types.js';

/** A run's numbers: one array per declared plot, `null` where there is no value. */
export type ScriptSeries = Readonly<Record<string, readonly (number | null)[]>>;

export interface ScriptOutput {
  readonly series: ScriptSeries;
  /**
   * Bars where a plot produced nothing, summed across plots.
   *
   * Surfaced rather than hidden. Warm-up gaps are normal — a 20-period average
   * has no value for nineteen bars — but a script that returns nothing at all
   * would otherwise draw an empty pane that looks identical to a script that is
   * simply off screen. The number lets the panel say which.
   */
  readonly gaps: number;
}

export interface ScriptOutputCheck {
  readonly ok: boolean;
  readonly error?: string;
  readonly output?: ScriptOutput;
}

/**
 * Validate and normalise one run's return value.
 *
 * NaN and Infinity become `null` rather than an error. This is the single most
 * common thing an indicator produces — `0/0` on the first bar, a log of zero, a
 * standard deviation of one sample — and failing the whole run over it would
 * reject most correct scripts on their first bar. They are counted into `gaps`
 * instead, so a script producing nothing but NaN is visible as such.
 *
 * Everything else is refused. A wrong LENGTH is refused rather than padded,
 * because a plot silently aligned to the wrong bars is a value drawn at a time
 * it did not happen — the one failure mode that looks entirely plausible on a
 * chart and is wrong in a way a trader would act on.
 */
export function validateScriptOutput(
  raw: unknown,
  plots: readonly ScriptPlotDecl[],
  barCount: number,
): ScriptOutputCheck {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return { ok: false, error: 'A script returns an object of plot id to array of values.' };
  }

  const record = raw as Record<string, unknown>;
  const declared = new Set(plots.map((p) => p.id));

  for (const key of Object.keys(record)) {
    if (!declared.has(key)) {
      return { ok: false, error: `The script returned "${key}", which it does not declare as a plot.` };
    }
  }

  const series: Record<string, (number | null)[]> = {};
  let gaps = 0;

  for (const plot of plots) {
    const values = record[plot.id];
    if (!Array.isArray(values)) {
      return { ok: false, error: `The script declares the plot "${plot.id}" but did not return an array for it.` };
    }
    if (values.length !== barCount) {
      return {
        ok: false,
        error: `"${plot.id}" has ${values.length} values for ${barCount} bars. Return one value per bar, using null where there is none.`,
      };
    }

    const column = new Array<number | null>(barCount);
    for (let i = 0; i < barCount; i += 1) {
      const v = values[i];
      if (typeof v === 'number' && Number.isFinite(v)) {
        column[i] = v;
      } else if (v === null || v === undefined || (typeof v === 'number' && !Number.isFinite(v))) {
        column[i] = null;
        gaps += 1;
      } else {
        return {
          ok: false,
          error: `"${plot.id}" returned ${typeof v} at bar ${i + 1}. A plot holds numbers, or null for no value.`,
        };
      }
    }
    series[plot.id] = column;
  }

  return { ok: true, output: { series, gaps } };
}

/**
 * True when a run produced no value anywhere.
 *
 * Its own function because the panel says something different for it — an empty
 * result is a script that ran without error and drew nothing, which reads as a
 * broken application unless it is named.
 */
export function scriptDrewNothing(output: ScriptOutput, plots: readonly ScriptPlotDecl[], barCount: number): boolean {
  return plots.length > 0 && barCount > 0 && output.gaps === plots.length * barCount;
}
